import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Plus, FileText } from 'lucide-react';
import { theme } from '../styles/theme';
import { Button, Input, Select, Label, ErrorMessage } from '../styles/GlobalStyles';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: ${theme.spacing[4]};
`;

const ModalContainer = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.xl};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  padding: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  font-family: ${theme.typography.fontFamily.display};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  border-radius: ${theme.borderRadius.lg};
  color: white;
`;

const ModalDescription = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  margin-top: ${theme.spacing[2]};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[100]};
    color: ${theme.colors.primary[600]};
  }
`;

const ModalContent = styled.div`
  padding: ${theme.spacing[6]};
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const HelpText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
  margin-top: ${theme.spacing[4]};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column-reverse;
  }
`;

export interface ProjectFormData {
  title: string;
  description: string;
  industry: string;
  priority: 'low' | 'medium' | 'high';
  estimatedValue: string;
  deadline: string;
}

interface AddProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ProjectFormData) => void;
  isLoading?: boolean;
}

const INDUSTRY_OPTIONS = [
  { value: '', label: 'Select Industry' },
  { value: 'agriculture_food', label: 'Agriculture & Food' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'aviation_aerospace', label: 'Aviation & Aerospace' },
  { value: 'banking_finance', label: 'Banking & Finance' },
  { value: 'biotechnology', label: 'Biotechnology' },
  { value: 'construction_infrastructure', label: 'Construction & Infrastructure' },
  { value: 'consulting_services', label: 'Consulting Services' },
  { value: 'cybersecurity', label: 'Cybersecurity' },
  { value: 'defense_military', label: 'Defense & Military' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'education_training', label: 'Education & Training' },
  { value: 'energy_utilities', label: 'Energy & Utilities' },
  { value: 'entertainment_media', label: 'Entertainment & Media' },
  { value: 'environmental_services', label: 'Environmental Services' },
  { value: 'fashion_retail', label: 'Fashion & Retail' },
  { value: 'government_public', label: 'Government & Public Services' },
  { value: 'healthcare_medical', label: 'Healthcare & Medical' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'legal_services', label: 'Legal Services' },
  { value: 'logistics_transportation', label: 'Logistics & Transportation' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'mining_resources', label: 'Mining & Resources' },
  { value: 'nonprofit_ngo', label: 'Non-profit & NGO' },
  { value: 'pharmaceuticals', label: 'Pharmaceuticals' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'research_development', label: 'Research & Development' },
  { value: 'technology_software', label: 'Technology & Software' },
  { value: 'telecommunications', label: 'Telecommunications' },
  { value: 'tourism_hospitality', label: 'Tourism & Hospitality' },
  { value: 'other', label: 'Other' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low Priority' },
  { value: 'medium', label: 'Medium Priority' },
  { value: 'high', label: 'High Priority' }
];

const AddProjectModal: React.FC<AddProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    title: '',
    description: '',
    industry: '',
    priority: 'medium',
    estimatedValue: '',
    deadline: ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ProjectFormData, string>>>({});

  // Close modal on escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Close modal when clicking on overlay
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: '',
        description: '',
        industry: '',
        priority: 'medium',
        estimatedValue: '',
        deadline: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ProjectFormData, string>> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Project title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Project description is required';
    }

    // Company ID no longer required - auto-set by backend

    if (!formData.industry) {
      newErrors.industry = 'Industry selection is required';
    }

    if (formData.deadline && new Date(formData.deadline) < new Date()) {
      newErrors.deadline = 'Deadline must be in the future';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = (field: keyof ProjectFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <div>
            <ModalTitle>
              <TitleIcon>
                <Plus size={20} />
              </TitleIcon>
              Add New Project
            </ModalTitle>
            <ModalDescription>
              Create a new project to organize your tender analyses and track progress.
            </ModalDescription>
          </div>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        
        <ModalContent>
          <FormContainer onSubmit={handleSubmit}>
            <FormField>
              <Label htmlFor="title">Project Title *</Label>
              <Input
                id="title"
                type="text"
                placeholder="e.g., Government Infrastructure Tender - Highway Development"
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                $hasError={!!errors.title}
                disabled={isLoading}
              />
              {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
            </FormField>

            <FormField>
              <Label htmlFor="description">Project Description *</Label>
              <Input
                id="description"
                as="textarea"
                rows={3}
                placeholder="Provide a brief description of the project scope and objectives..."
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                $hasError={!!errors.description}
                disabled={isLoading}
                style={{ resize: 'vertical', minHeight: '80px' }}
              />
              {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}
            </FormField>

            
              <FormField>
                <Label htmlFor="industry">Industry *</Label>
                <Select
                  id="industry"
                  value={formData.industry}
                  onChange={(e) => updateField('industry', e.target.value)}
                  $hasError={!!errors.industry}
                  disabled={isLoading}
                >
                  {INDUSTRY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                {errors.industry && <ErrorMessage>{errors.industry}</ErrorMessage>}
              </FormField>
            

            <FormRow>
              <FormField>
                <Label htmlFor="priority">Priority Level</Label>
                <Select
                  id="priority"
                  value={formData.priority}
                  onChange={(e) => updateField('priority', e.target.value as 'low' | 'medium' | 'high')}
                  disabled={isLoading}
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField>
                <Label htmlFor="estimatedValue">Estimated Value (Optional)</Label>
                <Input
                  id="estimatedValue"
                  type="text"
                  placeholder="e.g., £500,000"
                  value={formData.estimatedValue}
                  onChange={(e) => updateField('estimatedValue', e.target.value)}
                  disabled={isLoading}
                />
                <HelpText>Estimated contract value or budget</HelpText>
              </FormField>
            </FormRow>

            <FormField>
              <Label htmlFor="deadline">Project Deadline (Optional)</Label>
              <Input
                id="deadline"
                type="date"
                value={formData.deadline}
                onChange={(e) => updateField('deadline', e.target.value)}
                $hasError={!!errors.deadline}
                disabled={isLoading}
              />
              {errors.deadline && <ErrorMessage>{errors.deadline}</ErrorMessage>}
              <HelpText>Expected completion or submission deadline</HelpText>
            </FormField>

            <ButtonGroup>
              <Button
                type="button"
                $variant="ghost"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  'Creating...'
                ) : (
                  <>
                    <FileText size={16} />
                    Create Project
                  </>
                )}
              </Button>
            </ButtonGroup>
          </FormContainer>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default AddProjectModal;